// @ts-nocheck
import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Upload, Download, FileText, AlertCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/lib/supabase';
import Papa from 'papaparse';
import { getSessionUser } from '@/lib/authSession';

interface CampsCsvUploadProps {
  onUploadComplete: () => void;
}

interface CsvCampData {
  title: string;
  college_name: string;
  location?: string;
  start_date?: string;
  end_date?: string;
  description?: string;
  website_url?: string;
  sport?: string;
  gender?: string;
  division?: string;
  logo_url?: string;
}

const CampsCsvUpload: React.FC<CampsCsvUploadProps> = ({ onUploadComplete }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState<{ success: number; errors: string[] } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  // Generate CSV template
  const downloadTemplate = () => {
    const headers = [
      'title',
      'college_name', 
      'location',
      'start_date',
      'end_date',
      'description',
      'website_url',
      'sport',
      'gender',
      'division',
      'logo_url'
    ];
    
    const sampleData = [
      'Summer Football Camp',
      'University of Example',
      'Example City, ST',
      '2024-06-15',
      '2024-06-18',
      'Elite football training camp for high school athletes',
      'https://example.edu/football-camp',
      'Football',
      'Male',
      'D1',
      'https://example.edu/logo.png'
    ];

    const csvContent = [
      headers.join(','),
      sampleData.join(',')
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'camps_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast({
      title: 'Template Downloaded',
      description: 'CSV template has been downloaded successfully'
    });
  };

  // Validate camp data
  const validateCampData = (data: any): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];
    
    if (!data.title?.trim()) {
      errors.push('Title is required');
    }
    
    if (!data.college_name?.trim()) {
      errors.push('College name is required');
    }

    if (data.start_date && !isValidDate(data.start_date)) {
      errors.push('Invalid start date format (use YYYY-MM-DD)');
    }

    if (data.end_date && !isValidDate(data.end_date)) {
      errors.push('Invalid end date format (use YYYY-MM-DD)');
    }

    if (data.start_date && data.end_date && data.end_date < data.start_date) {
      errors.push('End date cannot be before start date');
    }

    if (data.website_url && data.website_url.match(/^https?:\/\/.+/) === null) {
      errors.push('Website must be a valid URL (starting with http:// or https://)');
    }

    return { isValid: errors.length === 0, errors };
  };

  const isValidDate = (dateString: string): boolean => {
    const date = new Date(dateString);
    return !isNaN(date.getTime()) && dateString.match(/^\d{4}-\d{2}-\d{2}$/) !== null;
  };

  // Process CSV upload
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast({
        title: 'Invalid File',
        description: 'Please select a CSV file',
        variant: 'destructive'
      });
      return;
    }

    setUploading(true);
    setUploadResults(null);

    try {
      // Get current user profile
      const { data: { user }, error: userError } = await getSessionUser();
      if (userError || !user) {
        throw new Error('Authentication required');
      }

      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', user.id)
        .single();
        
      if (profileError || !profileData) {
        throw new Error('User profile not found');
      }

      // Parse CSV - handle multi-line fields properly
      Papa.parse(file, {
        header: true,
        skipEmptyLines: 'greedy', // Skip all empty lines more aggressively
        quoteChar: '"', // Properly handle quoted fields with newlines
        escapeChar: '"', // Handle escaped quotes
        transformHeader: (header) => header.trim().toLowerCase().replace(/\s+/g, '_'),
        complete: async (results) => {
          const camps = results.data as CsvCampData[];
          const errors: string[] = [];
          let successCount = 0;

          for (let i = 0; i < camps.length; i++) {
            const camp = camps[i];
            const rowNumber = i + 2; // +2 because CSV is 1-indexed and has header row

            try {
              // Validate the camp data
              const validation = validateCampData(camp);
              if (!validation.isValid) {
                errors.push(`Row ${rowNumber}: ${validation.errors.join(', ')}`);
                continue;
              }

              // Clean description - normalize whitespace and newlines
              const cleanDescription = (desc: string | undefined) => {
                if (!desc) return null;
                return desc
                  .replace(/\r\n/g, '\n')
                  .replace(/\n{3,}/g, '\n\n') // Max 2 consecutive newlines
                  .trim();
              };

              // Prepare camp data for database
              const campData = {
                title: camp.title?.trim(),
                college_name: camp.college_name?.trim(),
                location: camp.location?.trim() || null,
                start_date: camp.start_date || null,
                end_date: camp.end_date || null,
                description: cleanDescription(camp.description),
                website_url: camp.website_url?.trim() || null,
                sport: camp.sport?.trim() || null,
                gender: camp.gender?.trim() || null,
                division: camp.division?.trim() || null,
                logo_url: camp.logo_url?.trim() || null,
                approval_status: 'approved',
                is_approved: true,
                created_by: profileData.id
              };

              // Insert camp into database
              const { error: insertError } = await supabase
                .from('college_camps')
                .insert([campData]);

              if (insertError) {
                errors.push(`Row ${rowNumber}: Database error - ${insertError.message}`);
              } else {
                successCount++;
              }

            } catch (error) {
              errors.push(`Row ${rowNumber}: ${error.message}`);
            }
          }

          setUploadResults({ success: successCount, errors });
          
          if (successCount > 0) {
            toast({
              title: 'Upload Complete',
              description: `Successfully uploaded ${successCount} camps${errors.length > 0 ? ` with ${errors.length} errors` : ''}`
            });
            onUploadComplete();
          } else {
            toast({
              title: 'Upload Failed',
              description: 'No camps were successfully uploaded',
              variant: 'destructive'
            });
          }

          setUploading(false);
        },
        error: (error) => {
          console.error('CSV parsing error:', error);
          toast({
            title: 'CSV Error',
            description: 'Failed to parse CSV file',
            variant: 'destructive'
          });
          setUploading(false);
        }
      });

    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: 'Upload Error',
        description: error.message || 'Failed to upload CSV',
        variant: 'destructive'
      });
      setUploading(false);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          CSV Import
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={downloadTemplate}
            className="flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            Download Template
          </Button>

          <div className="relative">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              disabled={uploading}
            />
            <Button
              variant="default"
              disabled={uploading}
              className="flex items-center gap-2"
            >
              <Upload className="h-4 w-4" />
              {uploading ? 'Uploading...' : 'Upload CSV'}
            </Button>
          </div>
        </div>

        <div className="text-sm text-muted-foreground space-y-1">
          <p><strong>Instructions:</strong></p>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li>Download the template to see the required format</li>
            <li>Required fields: title, college_name</li>
            <li>Date format: YYYY-MM-DD (e.g., 2024-06-15)</li>
            <li>URLs must start with http:// or https://</li>
            <li>All camps will be automatically approved upon upload</li>
          </ul>
        </div>

        {uploadResults && (
          <Card className="bg-background">
            <CardContent className="pt-4">
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-green-600">
                  <div className="h-2 w-2 bg-green-600 rounded-full"></div>
                  Successfully uploaded: {uploadResults.success} camps
                </div>
                
                {uploadResults.errors.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-red-600">
                      <AlertCircle className="h-4 w-4" />
                      Errors ({uploadResults.errors.length}):
                    </div>
                    <div className="text-sm space-y-1 max-h-40 overflow-y-auto">
                      {uploadResults.errors.map((error, index) => (
                        <div key={index} className="text-red-600 pl-6">
                          {error}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </CardContent>
    </Card>
  );
};

export default CampsCsvUpload;